import { Image } from '@mantine/core';
import { DiscountIcon, LogoIcon, StarIcon } from 'assets/Icons';
import { Empty } from 'components/atoms';
import { ProductSkeletons } from 'pages/Products';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface Props {
  vendors: any[];
  diva?: any;
  isLoading: boolean;
  skeletonCount?: number;
  emptyMessage?: string;
  showEmptyPrompt?: boolean;
  emptyText?: string;
}

const VendorsGrid: React.FC<Props> = ({
  diva,
  vendors,
  isLoading,
  skeletonCount,
  showEmptyPrompt = true,
  emptyText
}) => {
  const goto = useNavigate();
  const location = useLocation();

  // Extract location from business name (e.g., "Choplife (Ikeja)" -> "Ikeja")
  const extractLocation = (businessName: string | undefined): string | null => {
    if (!businessName) return null;
    const match = businessName.match(/\((.*?)\)$/);
    return match ? match[1].trim() : null;
  };

  // Get clean business name without location (e.g., "Choplife (Ikeja)" -> "Choplife")
  const getCleanBusinessName = (businessName: string | undefined): string => {
    if (!businessName) return 'Unnamed Vendor';
    return businessName.replace(/\s*\(.*?\)$/, '').trim() || businessName;
  };

  // ✅ FIXED: Always use vendor's specific locationId
  const generateVendorUrl = (vendor: any) => {
    if (!vendor?.seller?.id) return '#';

    const params = new URLSearchParams(location.search);

    // 1. Preserve the current category
    const currentCategory = params.get('category');
    if (currentCategory) {
      params.set('category', currentCategory);
    } else if (vendor.categoryId) {
      params.set('category', vendor.categoryId.toString());
    }

    // 2. Set vendor ID
    params.set('vendor', vendor.seller.id.toString());

    // 3. ✅ FIXED: Always use the vendor's specific locationId (don't preserve from URL)
    if (vendor.seller.locationId) {
      params.set('locationId', vendor.seller.locationId.toString());
    }

    // Final URL
    return `${location.pathname}?${params.toString()}`;
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {vendors.map((vendor, index) => {
          const locationLabel = extractLocation(vendor?.seller?.businessName);
          const cleanBusinessName = getCleanBusinessName(vendor?.seller?.businessName);

          return (
            <Link
              key={`vendor-${vendor?.seller?.id || index}`}
              to={generateVendorUrl(vendor)}
              className="block hover:opacity-90 transition-opacity"
            >
              <div className="relative w-full aspect-[4/3] bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100">
                {vendor?.sellerImage ? (
                  <Image
                    src={vendor.sellerImage}
                    alt={cleanBusinessName || 'Vendor logo'}
                    className="object-cover w-full h-full"
                    fit="cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-50">
                    <LogoIcon className="w-16 h-16 text-gray-300" />
                  </div>
                )}

                {/* Location Label */}
                {locationLabel && (
                  <div className="absolute top-2 right-2 bg-gray-900/80 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-lg">
                    {locationLabel}
                  </div>
                )}

                {/* Badges */}
                <div className="absolute top-2 left-2 flex flex-col gap-1.5">
                  {vendor?.seller?.verified && (
                    <div className="flex items-center gap-1 bg-yellow-500 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-md">
                      <StarIcon className="w-3.5 h-3.5" />
                      <span>Top</span>
                    </div>
                  )}
                  {vendor?.hasPromotions && (
                    <div className="flex items-center gap-1 bg-red-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-md">
                      <DiscountIcon className="w-3.5 h-3.5" />
                      <span>Sale</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Vendor Name (cleaned) */}
              <h3
                className="font-semibold text-sm md:text-base pt-3 line-clamp-2 leading-tight"
                title={cleanBusinessName}
              >
                {cleanBusinessName}
              </h3>

              {/* Category Tags */}
              <div className="flex gap-1.5 items-center flex-wrap mt-2">
                {vendor?.sub_categories?.slice(0, 3)?.map((category: string, i: number) => (
                  <span
                    key={i}
                    className="border border-secondary/30 text-[10px] bg-white/40 text-secondary px-2 py-0.5 rounded-md font-medium"
                  >
                    {category.replace(/_/g, ' ')}
                  </span>
                ))}
                {vendor?.sub_categories?.length > 3 && (
                  <span className="text-[10px] text-gray-500 font-medium">
                    +{vendor.sub_categories.length - 3} more
                  </span>
                )}
              </div>
            </Link>
          );
        })}

        {/* Loading State */}
        {isLoading && <ProductSkeletons count={skeletonCount ?? 10} />}

        {/* Empty State */}
        {!isLoading && vendors.length === 0 && (
          <div className="col-span-full">
            <Empty
              message={emptyText || 'No vendors found in this category.'}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default VendorsGrid;